import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Tracks', path: '/tracks' },
    { name: 'Speakers', path: '/speakers' },
    { name: 'For Authors', path: '/for-authors' },
    { name: 'Committee', path: '/committee' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <div className="min-h-screen">
      {/* Glassmorphism Navbar */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm'
            : 'bg-transparent'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <div className="font-outfit text-xl md:text-2xl font-bold text-slate-950 tracking-tight">
                IRASM<span className="text-[#B6671D]">-2027</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md font-inter text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-[#B6671D] text-white'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-200"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-4 space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-4 py-3 rounded-md font-inter text-sm font-medium transition-colors ${
                      location.pathname === item.path
                        ? 'bg-[#B6671D] text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="pt-16 md:pt-20">{children}</main>

      {/* Footer */}
      <footer className="bg-[#D3632D] text-white py-12 md:py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Conference Info */}
            <div>
              <h3 className="font-outfit text-xl font-bold mb-4">
                IRASM<span className="text-white">-2027</span>
              </h3>
              <p className="font-inter text-sm text-white/80 leading-relaxed">
                1st International Conference on Robotics, Automation and Smart Manufacturing
              </p>
              <p className="font-mono text-sm text-white/80 mt-2">19–20 February 2027</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-outfit text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/for-authors" className="font-inter text-sm text-white/80 hover:text-white transition-colors">
                    Submit Paper
                  </Link>
                </li>
                <li>
                  <Link to="/for-authors" className="font-inter text-sm text-white/80 hover:text-white transition-colors">
                    Register Now
                  </Link>
                </li>
                <li>
                  <Link to="/for-authors" className="font-inter text-sm text-white/80 hover:text-white transition-colors">
                    Author Guidelines
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
                    <h4 className="font-outfit text-lg font-semibold mb-4">Contact</h4>

                    <p className="font-inter text-sm text-white/80 leading-relaxed">
                      Department of Mechatronics Engineering<br />
                      Manipal University Jaipur<br />
                      Rajasthan, India
                    </p>

                    <div className="font-inter text-sm text-white/80 mt-3 space-y-1">
                  {/*     <a
                        href="mailto:Irasam.mechatronics@jaipur.manipal.edu"
                        className="block hover:text-white transition-colors"
                      >
                        Irasam.mechatronics@jaipur.manipal.edu
                      </a>
 */}
                      <a
                        href="mailto:manish.rawat@jaipur.manipal.edu"
                        className="block hover:text-white transition-colors"
                      >
                        manish.rawat@jaipur.manipal.edu
                      </a>

                      <a
                        href="mailto:krishna.pandey@jaipur.manipal.edu"
                        className="block hover:text-white transition-colors"
                      >
                        krishna.pandey@jaipur.manipal.edu
                      </a>
                    </div>
                  </div>
          </div>

          <div className="border-t border-white/20 mt-8 pt-8 text-center">
            <p className="font-inter text-sm text-white/80">
              © {new Date().getFullYear()} IRASM-2027. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;